import React, {useState, useEffect} from "react";
import {useVsiAppContext} from "../../../../stores/VsiApp";
import {
    Row,
    Col,
    ButtonGroup,
    Button,
    Input,
    Card,
    FormGroup,
    Label,
    UncontrolledTooltip, InputGroup, InputGroupText, Container
} from "reactstrap";
import {
    useAuthWithRoute,
    useDataWithRoute,
    useTitleFromComponent, useUrlState
} from "../../../../utils/hooks";
import {
    VsiBubble,
    VsiH4, VsiH6,
    VsiHr,
    VsiLgText, VsiMedLabel,
    VsiSmallText
} from "../../../../styled/App.styled";
import Stockchart from "../../../../components/Stockchart";
import CustomResourceCard from "../../../../components/capacityModeling/CustomResourceCard";
import CustomHostCard from "../../../../components/capacityModeling/CustomHostCard";
import HostCard from "../../../../components/capacityModeling/HostCard";
import {
    formatStockChartConfig,
    snakeCaseToTitleCase,
    returnDates,
    simpleGrowthRate,
    simpleForecast
} from "../../../../utils/Helpers";
import VsiLabeledSelect from "../../../../components/VsiLabeledSelect";
import dayjs from "dayjs";
import Grid from "../../../../components/Grid";
import {clusterModeling} from "../../../../utils/gridColumnDefs/virtual/clusterModeling";
import {DeleteAuth, PostAuth, PutAuth} from "../../../../utils/Verbs";
import VsiModal from "../../../../components/VsiModal";
import {roundToNearestHundredth} from "../../../../utils/math";
import {VsiSelectLabel} from "../../../../styled/VsiLabeledSelect.styled";
import {weightedGrowthRate} from "../../../../utils/growthRateForecastUtils";

const allTypes = [
    {
        name: "vcpu",
        label: "vCPU",
        chartDefs: [
            {name: "Virtual CPU Used", field: "total_virtual_cpus_used", grField: "cpu_used_growth"},
            {name: "Virtual CPU Capacity", field: "total_virtual_cpus", grField: "cpu_capacity_growth"},
            {name: "Virtual CPUs Allocated", field: "total_virtual_cpus_alloc", grField: "cpu_capacity_growth"}
        ]
    },
    {
        name: "memory",
        label: "Memory",
        chartDefs: [
            {name: "Memory Used (GiB)", field: "memory_used", grField: "memory_used_growth"},
            {name: "Memory Capacity (GiB)", field: "total_memory", grField: "memory_capacity_growth"}
        ]
    },
    {
        name: "disk",
        label: "Disk",
        chartDefs: [
            {name: "Disk Used (GiB)", field: "used", grField: "disk_used_growth"},
            {name: "Disk Capacity (GiB)", field: "allocated", grField: "disk_capacity_growth"}
        ]
    },
    {
        name: "vmbuild",
        label: "VM Build",
        chartDefs: [
            {name: "# of VMs", field: "num_vms", grField: "vm_build_growth"},
            {name: "VM Build Capacity", field: "vm_build_capacity", grField: "vm_build_capacity_growth"}
        ]
    },
    {
        name: "cost",
        label: "Cost",
        chartDefs: [
            {name: "Estimated Utilization Cost", field: "utilization_cost"},
            {name: "Cluster Cost", field: "total_cost"}
        ]
    }
];

export default function ClusterModeling({title}) {
    const {theme, client, periodId, getAccessTokenSilently, user} = useVsiAppContext();

    const [params, setParams] = useUrlState({
        cluster_name: "",
        vcenter_name: "",
        chart_type: "vcpu",
        resource_type: "capacity"
    });

    const {
        cluster_name: clusterName,
        vcenter_name: vCenterName,
        chart_type: chartType,
        resource_type: resourceType
    } = params;

    useTitleFromComponent(`${encodeURIComponent(clusterName) || "N/A"} - ${title}`);

    const [clusterOverviewData] = useDataWithRoute(
        `/api/v6/report/virtual_cluster_overview_proc?client_id=${client.clientId}&period_id=${periodId}&vcenter_name=${encodeURI(vCenterName)}&cluster_name=${encodeURIComponent(clusterName)}`
    );
    const [esxHostsData, {isLoading: isEsxHostsLoading}] = useDataWithRoute(
        `/api/v6/report/virtual_interactive_host_models_for_client_proc?client_id=${client.clientId}&period_id=${periodId}&device_name=${encodeURI(vCenterName)}&cluster_name=${encodeURIComponent(clusterName)}`
    );
    const [trendData, {isLoading: isTrendDataLoading}] = useDataWithRoute(
        `/api/v6/report/virtual_cluster_trend_proc?client_id=${client.clientId}&period_id=${periodId}&vcenter_name=${encodeURI(vCenterName)}&cluster_name=${encodeURIComponent(clusterName)}`
    );

    const [
        savedPlanData,
        {
            isLoading: isPlanDataLoading,
            reFetchData: reFetchSavedPlanData,
            setData: setSavedPlanData
        }
    ] = useAuthWithRoute(
        `/api/v6/admin/virtual_cluster_capacity_plan/${client.clientId}/${encodeURI(vCenterName)}/${encodeURI(clusterName)}`
    );

    const [chartTrendData, setChartTrendData] = useState([]);

    const [flattenFuture, setFlattenFuture] = useState(false);

    const [allDates, setAllDates] = useState([]);
    const [dateSelected, setDateSelected] = useState({});

    const [__allDataForecasted, setAllDataForecasted] = useState([]);
    const [allDataForecastedReversed, setAllDataForecastedReversed] = useState([]);
    const [allClusterForecasted, setAllClusterForecasted] = useState([]);
    const [activeCapacityPlan, setActiveCapacityPlan] = useState({}); // would need to do a get from mongo

    const [esxHostSearchTerm, setEsxHostSearchTerm] = useState("");
    const [projectName, setProjectName] = useState("");
    const [projectNotes, setProjectNotes] = useState("");

    const [esxHostList, setEsxHostList] = useState([]);
    const [esxHostsVisible, setEsxHostsVisible] = useState([]);

    const [customHost] = useState({}); // this should never change

    const [selectedEditHost, setSelectedEditHost] = useState({});
    const [isEditHostModalOpen, setIsEditHostModalOpen] = useState(false);

    const [capacityConstraint, setCapacityConstraint] = useState("");
    const [weeklyData, setWeeklyData] = useState([]);

    const handleCapacityPlan = (data) => {
        const capacity = {
            cpu: 0,
            mem: 0,
            disk: 0,
            vm: 0,
            cost: 0
        };

        const workload = {...capacity};
        const dateTrackingMetadata = {
            date: "",
            month: ""
        };

        return (data || []).map((datum) => {
            const newDatum = {...datum};
            const dateToCompare = dayjs(datum.collection_date).format("MM/YYYY");

            // no longer trending by month, so we just need to affect the first date of the month. this tracks that
            if (dateTrackingMetadata.month !== dateToCompare) {
                dateTrackingMetadata.month = dateToCompare;
                dateTrackingMetadata.date = datum.collection_date;
            }

            if (typeof activeCapacityPlan?.[dateToCompare]?.models === "object" && dateTrackingMetadata.date === datum.collection_date) {
                Object.keys(activeCapacityPlan[dateToCompare].models).forEach((modelKey) => {
                    const host = activeCapacityPlan[dateToCompare].models[modelKey];
                    const {
                        cpus,
                        memory_size,
                        disk_size,
                        modelCountAdded,
                        model_count,
                        cost_per_disk,
                        cost_per_cpu,
                        cost_per_memory
                    } = host;
                    let totalCpus = (cpus || 0) + datum.total_virtual_cpus
                    let totalMemory = (memory_size || 0) + datum.total_memory
                    let totalDisk = (disk_size || 0) + datum.allocated
                    let addedCpuCost = cost_per_cpu - 0.06
                    let addedMemoryCost = cost_per_memory - 0.06
                    let addedDiskCost = cost_per_disk - 0.06

                    const modelsToCount = host.isCustom ? (modelCountAdded + model_count) : modelCountAdded || 0;
                    capacity.cpu = capacity.cpu + parseFloat(cpus * modelsToCount);
                    capacity.mem = capacity.mem + parseFloat(memory_size * modelsToCount);
                    capacity.disk = capacity.disk + parseFloat(disk_size * modelsToCount);
                    if (host.isCustom) {
                        capacity.disk = capacity.disk + parseFloat(disk_size * modelsToCount);
                        capacity.cost = capacity.cost + parseFloat(capacity.disk * cost_per_disk);
                    }
                    capacity.cost = capacity.cost + parseFloat(totalMemory * addedMemoryCost + totalCpus * addedCpuCost + totalDisk * addedDiskCost);

                });
            }

            if (typeof activeCapacityPlan?.[dateToCompare]?.customCapacity === "object" && dateTrackingMetadata.date === datum.collection_date) {
                const {customCapacity} = activeCapacityPlan[dateToCompare];
                // note: might need to calibrate vCPU calculation like the first case, but will see
                capacity.cpu = capacity.cpu + customCapacity.cpu;
                capacity.mem = capacity.mem + customCapacity.mem;
                capacity.disk = capacity.disk + customCapacity.disk;
                // capacity.cost = capacity.cost + customCapacity.cost;
            }

            if (typeof activeCapacityPlan?.[dateToCompare]?.customWorkload === "object" && dateTrackingMetadata.date === datum.collection_date) {
                // do something
                const {customWorkload} = activeCapacityPlan[dateToCompare];

                // note: might need to calibrate vCPU calculation like the first case, but will see
                workload.cpu = workload.cpu + customWorkload.cpu;
                workload.mem = workload.mem + customWorkload.mem;
                workload.disk = workload.disk + customWorkload.disk;
                workload.vm = workload.vm + customWorkload.vm;
                // workload.cost = workload.cost + (customWorkload.cost ?? 0)
            }

            // capacity
            newDatum.total_virtual_cpus = newDatum.total_virtual_cpus + capacity.cpu;
            newDatum.total_memory = newDatum.total_memory + capacity.mem;
            newDatum.allocated = newDatum.allocated + capacity.disk;
            newDatum.vm_build_capacity = newDatum.vm_build_capacity + capacity.vm;
            newDatum.total_cost = newDatum.total_cost + capacity.cost;

            //workload
            newDatum.total_virtual_cpus_used = Math.max(newDatum.total_virtual_cpus_used + workload.cpu, 0);
            newDatum.memory_used = Math.max(newDatum.memory_used + workload.mem, 0);
            newDatum.used = Math.max(newDatum.used + workload.disk, 0);
            newDatum.num_vms = newDatum.num_vms + workload.vm;

            // calculate build cap based on updated newDatum
            let lowerConstrain = capacityConstraint.toLowerCase();
            if (!lowerConstrain.includes("null")) {
                let constrainKey = null;
                if (lowerConstrain.includes("disk")) {
                    constrainKey = "allocated";
                } else if (lowerConstrain.includes("memory")) {
                    constrainKey = "total_memory";
                } else {
                    constrainKey = "total_virtual_cpus";
                }
            }

            return newDatum;
        });
    };

    const calculateHostCostWithTrendData = (hosts, datum) => {
        let totalCost = 0;
        let utilCost = 0;
        hosts.forEach(host => {
            totalCost += datum.total_virtual_cpus * (host.cost_per_cpu * host.model_count);
            totalCost += datum.total_memory * (host.cost_per_memory * host.model_count);
            totalCost += datum.allocated * (host.cost_per_disk * host.model_count);

            utilCost += datum.total_virtual_cpus_used * host.cost_per_cpu;
            utilCost += datum.used * host.cost_per_disk;
            utilCost += datum.memory_used * host.cost_per_memory;
        });

        return {
            total_cost: totalCost,
            utilization_cost: utilCost
        };
    };

    const handleDataWithCapacityPlan = (data) => {
        const allFieldsForForecast = [];
        const allCapacityFields = [];
        const allFieldsGrObj = {};
        const lastNonForecasted = data.length ? data[data.length - 1] : {};

        allTypes.forEach((type) => {
            type.chartDefs.forEach((def) => {
                if (def.field) {
                    allFieldsForForecast.push(def.field);
                    if (def.grField) {
                        allFieldsGrObj[def.field] = def.grField;
                    }
                    if (def.name.toLowerCase().includes("capacity")) {
                        allCapacityFields.push(def.field);
                    }
                }
            });
        });

        let clusterData = data.map((datum) => {
            return {
                ...datum,
                ...calculateHostCostWithTrendData(esxHostsData?.recordset ?? [], datum)
            };
        });

        const growths = {};
        allFieldsForForecast.forEach(field =>
            growths[field] = weightedGrowthRate(clusterData.map(m => ({
                date: m.collection_date,
                value: m[field]
            }))) * 7 //weekly
        );
        // I need to see the growth when testing with Jeff to prove the calculation is correct
        // so I am leaving this console.log for now.
        // Ashley that's for you hahaha
        console.log(growths);
        const forecastedData = simpleForecast(clusterData, allFieldsForForecast, growths, 26);

        const newData = [...clusterData, ...handleCapacityPlan(forecastedData)];

        const withPlanData =
            newData.map(val => {
                let returnVal = {...val};

                if (flattenFuture && returnVal.isForecasted) {
                    allCapacityFields.forEach(field => {
                        returnVal[field] = lastNonForecasted[field];
                    });
                }

                return {
                    ...returnVal,
                    vm_build_capacity: Math.round(returnVal.vm_build_capacity),
                    num_vms: Math.round(returnVal.num_vms)
                };
            });

        const formattedData = formatStockChartConfig(
            withPlanData,
            allTypes.find((type) => type.name === chartType)?.chartDefs || []
        );

        setChartTrendData(formattedData.series.map((series) => ({
            ...series,
            zoneAxis: "x",
            zones: [
                {
                    value: Date.parse(data?.[data.length - 1]?.collection_date?.split?.("T")?.[0])
                },
                {
                    dashStyle: "Dash"
                }
            ]
        })));
        setAllDataForecasted(withPlanData);
        setAllDataForecastedReversed([...withPlanData].reverse());
    };

    const changeDataWithModel = (host) => {
        const activePlan = {
            ...activeCapacityPlan
        };
        activePlan[dateSelected.value] = {
            ...(activePlan?.[dateSelected.value] || {}),
            costChange: 0,
            models: {
                ...(activePlan?.[dateSelected.value]?.models ?? {}),
                [host.model]: {
                    ...host,
                    ...(activePlan?.[dateSelected.value]?.models?.[host.model] || {}),
                    modelCountAdded: host.modelCountAdded
                }
            }
        };

        setActiveCapacityPlan(activePlan);
    };

    const changeDataWithCustomResource = (customInfo) => {
        const activePlan = {
            ...activeCapacityPlan
        };
        const newInfo = {
            ...customInfo
        };

        activePlan[dateSelected.value] = {
            ...(activePlan?.[dateSelected.value] || {}),
            [`custom${snakeCaseToTitleCase(resourceType)}`]: {
                ...(activePlan?.[dateSelected.value]?.[
                    `custom${snakeCaseToTitleCase(resourceType)}`
                    ] ?? {}),
                ...newInfo
            }
        };

        setActiveCapacityPlan(activePlan);
    };

    const handleDateChange = (date) => {
        setDateSelected(date);
    };

    const formatSavedData = (data, esxHosts) => {
        const foundHostModels = {}; // for faster lookups
        const savedPlan = data?.[0];

        const activePlan = {};
        if (savedPlan?._id) {
            (savedPlan?.adjustments ?? []).forEach((adjustment) => {
                const models = {};
                if (!activePlan[adjustment.actualDate]) {
                    activePlan[adjustment.actualDate] = {};
                }
                activePlan[adjustment.actualDate] = {...adjustment};
                (adjustment?.models ?? []).forEach((model) => {
                    if (model.model) {
                        if (!models[model.model]) {
                            models[model.model] = {};
                        }
                        if (!foundHostModels[model.model]) {
                            const hostInfo = (esxHosts ?? []).find((host) => host.model === model.model) || {};
                            const savedHostInfo = (adjustment?.models ?? []).find(savedHost => savedHost.model === model.model)
                            if (savedHostInfo) {
                                foundHostModels[model.model] = {...hostInfo, ...savedHostInfo}
                            } else {
                                foundHostModels[model.model] = {...hostInfo};
                            }
                        }

                        models[model.model] = {
                            ...foundHostModels[model.model],
                            modelCountAdded: model.modelCountAdded
                        };
                    }
                });
                activePlan[adjustment.actualDate].models = models;
            });
            let planModels = []
            Object.keys(activePlan?.[dateSelected.value]?.models || {}).forEach(modelKey => {
                planModels.push(activePlan[dateSelected.value].models[modelKey])
            })
            setEsxHostsVisible(planModels)
        }
        setActiveCapacityPlan(activePlan);
        setFlattenFuture(savedPlan?.flattenFuture ?? false);
    };

    useEffect(() => {
        formatSavedData(savedPlanData, esxHostList);
    }, [savedPlanData]);

    useEffect(() => {
        const allVisibleEsxHosts = esxHostsData?.recordsets?.[0] ?? [];
        let hostsList = [...(savedPlanData?.[0]?.customHostModels || []), ...allVisibleEsxHosts]
        setEsxHostList(hostsList);
    }, [esxHostsData, savedPlanData]);

    useEffect(() => {
        const allVisibleEsxHosts = esxHostList.filter?.(
            ({model}) => model.toLowerCase().includes(esxHostSearchTerm.toLowerCase())
        ) ?? [];

        let adjustmentModels = []
        savedPlanData?.[0]?.adjustments.map(adjustment => {
            if (adjustment.actualDate === dateSelected.value) {
                adjustmentModels = adjustment.models
            }
        })
        // Array of model objects
        let models = []
        if (adjustmentModels.length === 0) {
            models.push(...allVisibleEsxHosts);
        } else {
            allVisibleEsxHosts.forEach(model => {
                const savedModel = adjustmentModels.find(saved => saved.model === model.model);
                models.push(savedModel ? {...model, ...savedModel} : model);
            });
        }
        setEsxHostsVisible(models);
    }, [esxHostList, esxHostSearchTerm]);

    // Run once to get avg value of cpu/mem/disk per Cluster for resource card

    async function calculateAvgFromCluster() {
        const cluster_arr = clusterOverviewData?.recordsets?.[2] ?? [];
        let vm_cpu_size = 0;
        let vm_memory_size = 0;
        let vm_disk_size = 0;
        let vm_cost = 0;
        let vm_cpu_cost = 0;
        let vm_memory_cost = 0;
        let vm_disk_cost = 0;

        for (let i = 0; i < cluster_arr.length; i++) {
            vm_cpu_size += cluster_arr[i].num_cpus ?? 0;
            vm_memory_size += cluster_arr[i].memory_allocated ?? 0;
            vm_disk_size += cluster_arr[i].disk_allocated ?? 0;
            vm_cost += cluster_arr[i].monthly_total_cost ?? 0;
            vm_cpu_cost += cluster_arr[i].monthly_cpu_cost ?? 0;
            vm_memory_cost += cluster_arr[i].monthly_memory_cost ?? 0;
            vm_disk_cost += cluster_arr[i].monthly_disk_cost ?? 0;
        }
        const avg_vm_cpu_size = Math.round(vm_cpu_size / cluster_arr.length);
        const avg_vm_memory_size = Math.round(vm_memory_size / cluster_arr.length);
        const avg_vm_disk_size = Math.round(vm_disk_size / cluster_arr.length);
        const vm_avg_cost = vm_cost / cluster_arr.length;
        const avg_cpu_cost = vm_cpu_cost / cluster_arr.length;
        const avg_memory_cost = vm_memory_cost / cluster_arr.length;
        const avg_disk_cost = vm_disk_cost / cluster_arr.length;

        const clusterData = [
            {
                average_vm_cpu_size: avg_vm_cpu_size ?? 0,
                average_vm_memory_size: avg_vm_memory_size ?? 0,
                average_vm_disk_size: avg_vm_disk_size ?? 0,
                vm_avg_cost: vm_avg_cost ?? 0,
                avg_cpu_cost: avg_cpu_cost ?? 0,
                avg_memory_cost: avg_memory_cost ?? 0,
                avg_disk_cost: avg_disk_cost ?? 0
            }
        ];

        setAllClusterForecasted(clusterData);
    }

    useEffect(() => {
        if (clusterOverviewData?.recordsets?.[2]?.length > 0) {
            try {
                calculateAvgFromCluster();
            } catch (error) {
                console.error(`setAllClusterForecasted -- failed ${error}`);
            }
        }
        if (clusterOverviewData?.recordsets?.[0]?.length > 0) {
            setCapacityConstraint(clusterOverviewData?.recordsets?.[0]?.[0]?.capacity_constrain_str);
        }
    }, [clusterOverviewData]);

    useEffect(() => {
        if (trendData?.recordsets?.[0] && clusterOverviewData?.recordsets?.[0]) {
            handleDataWithCapacityPlan(trendData?.recordsets?.[0] || []);
        }
        if (savedPlanData && !projectName && !projectNotes) {
            setProjectName(savedPlanData?.[0]?.projectName)
            setProjectNotes(savedPlanData?.[0]?.notes)
        }
        setWeeklyData(trendData?.recordsets?.[1]);
    }, [
        trendData,
        chartType,
        activeCapacityPlan,
        clusterOverviewData,
        flattenFuture
    ]);

    useEffect(() => {
        if (!allDates.length && trendData?.recordsets?.[0]) {
            const trends = trendData?.recordsets?.[0];
            const dates = returnDates(trends?.[trends.length - 1]?.collection_date);
            setAllDates(dates);
            setDateSelected(dates[0]);
        }

    }, [trendData]);

    const onChartTypeChange = (type) => {
        setParams({
            chart_type: type.name,
        })
    };

    const onResourceTypeChange = (type) => {
        setParams({
            resource_type: type,
        })
    };

    const onFormSubmit = async () => {
        const adjustments = [];
        Object.keys(activeCapacityPlan).forEach((planKey) => {
            const models = [];
            (Object.keys(activeCapacityPlan?.[planKey]?.models ?? {}) ?? []).forEach(
                (modelKey) => {
                    if (activeCapacityPlan[planKey].models[modelKey].modelCountAdded !== 0) {
                        models.push(activeCapacityPlan[planKey].models[modelKey]);
                    }
                }
            );

            const adjustment = {
                ...activeCapacityPlan[planKey],
                actualDate: planKey
            };

            let costChange = 0;
            for (let i = 0; i < models.length; i++) {
                if (models[i]._id === 0) delete models[i]._id;

                const {
                    cost_per_cpu,
                    cost_per_disk,
                    cost_per_memory,
                    memory_size,
                    disk_size,
                    cpus,
                    modelCountAdded
                } = models[i];
                const cpu = ((cost_per_cpu * cpus) || 0);
                const defaultCpu = (0.06 * cpus) || 0;
                const disk = ((cost_per_disk * disk_size) || 0);
                const defaultDisk = (0.06 * disk_size) || 0;
                const mem = ((cost_per_memory * memory_size) || 0);
                const defaultMemory = (0.06 * memory_size) || 0;
                const total = cpu + disk + mem;
                const originalTotal = defaultCpu + defaultDisk + defaultMemory;

                let rounded;
                if (modelCountAdded) {
                    rounded = roundToNearestHundredth(total * modelCountAdded);
                } else if (!modelCountAdded) {
                    rounded = roundToNearestHundredth(total - originalTotal);
                } else {
                    rounded = roundToNearestHundredth((total - originalTotal) + (total * modelCountAdded))
                }
                costChange += rounded
            }
            adjustment.costChange = costChange

            if (models.length > 0) {
                adjustment.models = models;
            }
            adjustments.push(adjustment);
        });

        const existingHostModels = savedPlanData?.[0]?.customHostModels?.map?.(host => host.model) ?? [];
        const customHostModels = esxHostList.filter(host => {
            return host.isCustom && !existingHostModels.includes(host.model);
        });

        customHostModels.map(model => {
            const {cost_per_cpu, cost_per_disk, cost_per_memory, memory_size, disk_size, cpus, model_count} = model;
            const cpu = ((cost_per_cpu * cpus) || 0);
            const disk = ((cost_per_disk * disk_size) || 0);
            const mem = ((cost_per_memory * memory_size) || 0);
            const total = cpu + disk + mem;

            let rounded = roundToNearestHundredth(total * model_count);
            model.costChange = rounded
        })

        const planData = {
            deviceName: decodeURI(vCenterName),
            clusterName: decodeURI(clusterName),
            clientId: client.clientId,
            changedByUserEmail: user.email,
            notes: projectNotes,
            flattenFuture: flattenFuture,
            projectName,
            adjustments,
            customHostModels: [...(customHostModels ?? []), ...(savedPlanData?.[0]?.customHostModels ?? [])]
        };

        try {
            const planId = savedPlanData?.[0]?._id;
            const token = await getAccessTokenSilently();
            if (planId) {
                await PutAuth(
                    `/api/v6/admin/virtual_cluster_capacity_plan/${planId}`,
                    planData,
                    token
                );
                reFetchSavedPlanData();
            } else {
                await PostAuth(
                    `/api/v6/admin/virtual_cluster_capacity_plan`,
                    planData,
                    token
                );
                reFetchSavedPlanData();
            }
        } catch (error) {
            console.error(error);
        }
    };

    const onUndoClick = async () => {
        formatSavedData(savedPlanData, esxHostList);
    };

    const onDeleteClick = async () => {
        const planId = savedPlanData?.[0]?._id;
        if (planId) {
            try {
                const token = await getAccessTokenSilently();
                await DeleteAuth(
                    `/api/v6/admin/virtual_cluster_capacity_plan/${planId}`,
                    token
                );
                setActiveCapacityPlan({});
                setSavedPlanData([]);
                setProjectNotes("");
                setProjectName("");
                setFlattenFuture(false);
            } catch (error) {
                console.error(error);
            }
        }
    };

    const addCustomHost = host => {
        const newHostList = [host, ...esxHostList];
        setEsxHostList(newHostList);
    };

    const deleteHost = (e, hostModel) => {
        e.preventDefault();
        e.stopPropagation();
        const newHostList = esxHostList.filter(({model}) => model !== hostModel);
        setEsxHostList(newHostList);
    };

    const onEditHostChange = e => {
        const {name, value} = e.target;
        setSelectedEditHost(prevState => ({
            ...prevState,
            [name]: parseFloat(value)
        }));
    };

    const onHostCardClick = host => {
        setSelectedEditHost(host);
        setIsEditHostModalOpen(true);
    };

    const onHostEditOkay = () => {
        const newHostList = esxHostList.map((host) => {
            const newHost = {...host};
            if (host.model === selectedEditHost.model) {
                return selectedEditHost;
            }
            return newHost;
        });
        let activePlan = {};
        activePlan[dateSelected.value] = {actualDate: dateSelected.value};
        activePlan[dateSelected.value].models = [selectedEditHost]

        setActiveCapacityPlan({...activeCapacityPlan, ...activePlan})
        setEsxHostList(newHostList);
        setSelectedEditHost({});
        setIsEditHostModalOpen(false);
    };

    const calcShortCost = (costField, valueField) => {
        return (roundToNearestHundredth((selectedEditHost?.[costField] ?? 0) * (selectedEditHost?.[valueField] ?? 1)).toLocaleString());
    };

    const calcHostTotal = () => {
        const {cost_per_cpu, cost_per_disk, cost_per_memory, memory_size, disk_size, cpus} = selectedEditHost;
        const cpu = ((cost_per_cpu * cpus) || 0);
        const disk = ((cost_per_disk * disk_size) || 0);
        const mem = ((cost_per_memory * memory_size) || 0);
        const total = cpu + disk + mem;

        return roundToNearestHundredth(total).toLocaleString();
    };

    return (
        <Card className={`report-card p-2 nav-card-${theme}`}>
            <VsiH4>
                Cluster Modeling -{" "}
                {decodeURIComponent(clusterName) || "N/A"}
            </VsiH4>
            <Row>
                <Col xs={12} md={3} className={"mb-2"}>
                    <VsiBubble suppressCollapse>
                        <ButtonGroup className={"w-100 mb-1"}>
                            <Button
                                disabled={resourceType === "capacity"}
                                onClick={() => onResourceTypeChange("capacity")}
                            >
                                Capacity
                            </Button>
                            <Button
                                disabled={resourceType === "workload"}
                                onClick={() => onResourceTypeChange("workload")}
                            >
                                Workload
                            </Button>
                        </ButtonGroup>
                        <VsiLabeledSelect
                            options={allDates}
                            isDisabled={
                                isEsxHostsLoading || isTrendDataLoading || isPlanDataLoading
                            }
                            label={"Month to Add Resource:"}
                            minWidth={"100%"}
                            onChange={handleDateChange}
                            defaultValue={dateSelected}
                        />
                        <VsiLgText textAlign={"left"}>Project Name:</VsiLgText>
                        <Input
                            value={projectName}
                            onChange={({target}) => setProjectName(target.value)}
                            color={theme}
                        />
                        <VsiLgText textAlign={"left"}>Notes:</VsiLgText>
                        <Input
                            name="text"
                            value={projectNotes}
                            onChange={({target}) => setProjectNotes(target.value)}
                            type="textarea"
                        />
                        <VsiHr/>
                        <div className={"w-100 d-flex flex-row justify-content-between"}>
                            <ButtonGroup>
                                <Button color={"danger"} id={"clear-btn-plan"} onClick={onDeleteClick}>
                                    <i className={"fa-solid fa-trash"}/>
                                </Button>
                                <Button color={"info"} id={"reset-btn-plan"} onClick={onUndoClick}>
                                    <i className={"fa-solid fa-rotate-left"}/>
                                </Button>
                            </ButtonGroup>
                            <UncontrolledTooltip placement={"bottom"} target={"clear-btn-plan"}>
                                Clear saved adjustments
                            </UncontrolledTooltip>
                            <UncontrolledTooltip placement={"bottom"} target={"reset-btn-plan"}>
                                Undo local adjustments
                            </UncontrolledTooltip>
                            <ButtonGroup className={"me-2"}>
                                <Button color={"success"} id={"save-btn-plan"} onClick={onFormSubmit}>
                                    <i className={"fa-solid fa-floppy-disk"}/>
                                </Button>
                            </ButtonGroup>
                            <UncontrolledTooltip placement={"bottom"} target={"save-btn-plan"}>
                                Save adjustments
                            </UncontrolledTooltip>
                        </div>
                    </VsiBubble>
                    <VsiHr/>
                    <CustomResourceCard
                        resourceType={resourceType}
                        changeDataWithCustomResource={changeDataWithCustomResource}
                        chartType={chartType}
                        // workloadMetrics={allDataForecasted?.[0] ?? {}}
                        workloadMetrics={
                            allClusterForecasted?.[0] ?? [
                                {
                                    // default
                                    average_vm_cpu_size: 1,
                                    average_vm_memory_size: 4,
                                    average_vm_disk_size: 4,
                                    vm_avg_cost: 0
                                }
                            ]
                        }
                        activeCustomPlan={
                            activeCapacityPlan?.[dateSelected.value]?.[
                                `custom${snakeCaseToTitleCase(resourceType)}`
                                ] ?? {}
                        }
                    />
                    {resourceType === "capacity" ? (
                        <>
                            <CustomHostCard host={customHost} addCustomHost={addCustomHost}/>
                            <VsiHr/>
                            <VsiBubble suppressCollapse>
                                <Row>
                                    <Col xs={8} className={"d-flex flex-row justify-content-between"}>
                                        <FormGroup>
                                            <Label for={"hostSearch"}>
                                                ESX hosts: {esxHostList?.length ?? 0}
                                            </Label>
                                            <Input
                                                placeholder={"Search"}
                                                color={theme}
                                                id={"hostSearch"}
                                                value={esxHostSearchTerm}
                                                onChange={({target: {value}}) =>
                                                    setEsxHostSearchTerm(value)
                                                }
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col xs={12}>
                                        <VsiHr/>
                                    </Col>
                                </Row>
                                {esxHostsVisible?.map?.(
                                    (
                                        host,
                                        index // TODO: These numbers aren't adding up correctly
                                    ) => (
                                        <HostCard
                                            key={`host-${index}`}
                                            host={host}
                                            changeDataWithModel={changeDataWithModel}
                                            chartType={chartType}
                                            onCardClick={onHostCardClick}
                                            activeCapacityPlan={
                                                activeCapacityPlan?.[dateSelected.value]?.models?.[
                                                    host.model
                                                    ] ?? {}
                                            }
                                            deleteHost={(e) => deleteHost(e, host.model)}
                                        />
                                    )
                                )}
                            </VsiBubble>
                        </>
                    ) : null}
                </Col>
                <Col xs={12} md={9}>
                    <Row>
                        <Col xs={12}>
                            {/* <VsiBubble autobotName='Stockchart' unit={chartType}> */}
                            <VsiBubble noAutobot>
                                <Row>
                                    {chartType === "cost" ? (
                                        <Col>
                                            <VsiSmallText>This is an estimate.</VsiSmallText>
                                        </Col>
                                    ) : null}
                                    <Col className={"d-flex flex-row justify-content-start align-items-start"}>
                                        <Container>
                                            <VsiSmallText style={{margin: 0}}>
                                                Capacity Constraint:
                                            </VsiSmallText>
                                            <VsiH6 style={{margin: 0}}>
                                                * {capacityConstraint}
                                            </VsiH6>
                                        </Container>
                                    </Col>
                                    <Col className={"d-flex flex-row justify-content-end"}>
                                        <ButtonGroup>
                                            {allTypes.map((type, index) => (
                                                <Button
                                                    disabled={chartType === type.name}
                                                    key={`chart-btn-${index}-${type.name}`}
                                                    onClick={() => onChartTypeChange(type)}
                                                >
                                                    {type.label}
                                                </Button>
                                            ))}
                                        </ButtonGroup>
                                    </Col>
                                </Row>
                                {chartType !== "cost" ? (
                                    <Stockchart series={chartTrendData}/>
                                ) : (
                                    <Stockchart
                                        series={chartTrendData}
                                        formatLabels={"${value:,f}"}
                                        formatData={"${point.y:,.2f}"}
                                        formatPoint={"{series.name}: <b>${point.y}</b>"}
                                    />
                                )}
                                <div className="d-flex flex-row align-items-start">
                                    <div
                                        className="ms-2 d-flex flex-column align-items-center justify-content-center me-2">
                                        <VsiSelectLabel htmlFor="flattenFuture">
                                            Flatten Future Capacity
                                        </VsiSelectLabel>
                                        <FormGroup switch>
                                            <Input
                                                type="switch"
                                                role="switch"
                                                id="flattenFuture"
                                                checked={flattenFuture}
                                                onChange={() => setFlattenFuture(!flattenFuture)}
                                            />
                                        </FormGroup>
                                    </div>
                                </div>
                            </VsiBubble>
                        </Col>
                        <Col xs={12}>
                            <VsiBubble>
                                <Grid
                                    data={allDataForecastedReversed}
                                    columnDefs={clusterModeling}
                                />
                            </VsiBubble>
                        </Col>
                    </Row>
                </Col>
            </Row>
            <VsiModal
                toggle={setIsEditHostModalOpen}
                isOpen={isEditHostModalOpen}
                header={
                    <>
                        {selectedEditHost?.model} cost ($) per:
                    </>}
                footer={
                    <div className={"d-flex flex-row justify-content-between w-100"}>
                        <div className={"d-flex flex-column"}>
                            <VsiMedLabel>Total cost per host:</VsiMedLabel>
                            <VsiLgText>${calcHostTotal()}</VsiLgText>
                        </div>
                        <ButtonGroup>
                            <Button onClick={onHostEditOkay}>Done</Button>
                            <Button onClick={() => setIsEditHostModalOpen(false)} color={"danger"}>Cancel</Button>
                        </ButtonGroup>
                    </div>}
            >
                <>
                    <VsiMedLabel textAlign={"left"}>
                        CPU <VsiSmallText>(${calcShortCost("cost_per_cpu", "cpus")})</VsiSmallText>
                    </VsiMedLabel>
                    <InputGroup>
                        <InputGroupText>$</InputGroupText>
                        <Input value={selectedEditHost?.cost_per_cpu}
                               type={"number"}
                               onChange={onEditHostChange}
                               name={"cost_per_cpu"}/>
                    </InputGroup>
                    <VsiMedLabel textAlign={"left"}>
                        Memory <VsiSmallText>(${calcShortCost("cost_per_memory", "memory_size")})</VsiSmallText>
                    </VsiMedLabel>
                    <InputGroup>
                        <InputGroupText>$</InputGroupText>
                        <Input value={selectedEditHost?.cost_per_memory}
                               type={"number"}
                               onChange={onEditHostChange}
                               name={"cost_per_memory"}/>
                    </InputGroup>
                    <VsiMedLabel textAlign={"left"}>
                        Disk <VsiSmallText>(${calcShortCost("cost_per_disk", "disk_size")})</VsiSmallText>
                    </VsiMedLabel>
                    <InputGroup>
                        <InputGroupText>$</InputGroupText>
                        <Input value={selectedEditHost?.cost_per_disk}
                               type={"number"}
                               onChange={onEditHostChange}
                               name={"cost_per_disk"}/>
                    </InputGroup>
                </>
            </VsiModal>
        </Card>
    );
}
